import { useSWR } from '@tiger/swr';
import { FC, useMemo } from 'react';
import ErrorBox from 'components/ErrorBox';
import Loading from 'components/Loading';
import { ModalHandler } from 'components/Modal';
import { getWayIcon, getWayName, getWayPath } from 'modules/Deposit/helper';
import {
    IRAContributionWays,
    RecommendCurrencyEnum,
    RecommendDepositBankBase,
    RecommendDepositEnum
} from 'modules/Deposit/type';
import { hasIRAAccountAndLiveAccount } from 'stores/helper';
import security from 'stores/security';
import API from 'utils/API';
import iconCheck from './images/icon-check.svg';
import style from './style.scss';

interface WayListProps extends ModalHandler {
    /**
     * 当前的入金方式
     */
    currentWay: IRAContributionWays;
    /**
     * useNavigate返回的方法，该组件会在 openNativeModal 中打开，和外面的 Route context 不同，不能直接在组件中使用 useNavigate
     * @param to 目标路径
     */
    onItemClick: (to: string) => void;
}

const WayList: FC<WayListProps> = ({ currentWay, onItemClick, close }) => {
    const { data, isLoading, error, mutate } = useSWR<RecommendDepositBankBase[]>(
        {
            url: API.DEPOSIT.depositWay(),
            params: {
                license: security.webAccountInfo?.clearing?.toUpperCase(),
                customer_id: security.currentCustomerId,
                account_type: 'bs'
            }
        },
        {
            dataChain: resp => resp.data
        }
    );

    const list: Array<{
        icon: string;
        title: string;
        to: string;
        isCurrent: boolean;
    }> = useMemo(() => {
        if (!data) {
            return [];
        }

        return [
            ...data.map(item => {
                let wayPath = getWayPath(item.type, true);

                if (item.type === RecommendDepositEnum.Transfer && item.currency_type.length) {
                    wayPath = `/profile/deposit/${
                        item.currency_type.includes(RecommendCurrencyEnum.USD) ? 'USD' : item.currency_type[0]
                    }`;
                }

                return {
                    icon: getWayIcon(item.type, true),
                    title: getWayName(item.type, true),
                    isCurrent: item.type === currentWay,
                    to: wayPath
                };
            }),
            hasIRAAccountAndLiveAccount() && {
                icon: getWayIcon('contribution', true),
                title: __('Individual Account Contribution'),
                isCurrent: currentWay === 'transfer',
                to: '/profile/deposit/transfer'
            },
            {
                icon: getWayIcon('check', true),
                title: __('Check Contribution'),
                isCurrent: currentWay === 'check',
                to: '/profile/deposit/check'
            },
            {
                icon: getWayIcon('transfer', true),
                title: __('Other brokers'),
                isCurrent: false,
                to: '/profile/transfer'
            }
        ].filter(item => item && !item.isCurrent) as typeof list;
    }, [data, currentWay]);

    const handleNavigate = (to: string) => {
        onItemClick(to);
        close();
    };

    const render = () => {
        if (isLoading) {
            return <Loading />;
        }

        if (error) {
            return <ErrorBox error={error} onClick={() => mutate()} />;
        }

        return (
            <div className={style.list}>
                {list.map((item, index) => (
                    <div key={index} className={style.listItem}>
                        <div className={style.itemWrapper} onClick={() => handleNavigate(item.to)}>
                            <img src={item.icon} alt="icon" className={style.icon} />
                            <span>{item.title}</span>
                            {item.isCurrent && <img src={iconCheck} alt="check" className={style.iconCheck} />}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return <div className={style.modalRoot}>{render()}</div>;
};

export default WayList;
